/**
 * SurveyService.ts
 *
 * Fixed:
 *  - Removed deprecated CategoryModel, TransportationModel, SocialPreferenceModel imports
 *  - saveOnboardingDataIfHasLocation accepts userId as 3rd param
 *  - Uses Firebase Realtime Database (push to onboardingData) not Firestore addDoc
 */
import { ref, push } from "firebase/database";
import { sanitizeRecord } from "./PrivacyService";
import { database } from "../firebase";

export interface Profile {
  homeAddress: string;
  workAddress: string;
}

// ── Instruction builders (used by DirectUpload) ────────────────────────────

const baseInstruction = `
Act as a data scientist with expertise in Google APIs, particularly Google Maps and Places. Your primary role is to analyze and interpret user search data to create a profile focusing on transportation habits. Adopt a supportive, trustworthy, and approachable demeanor, using your strong analytical capabilities and understanding of user behavior to deliver precise results.
`;

const task = `
1. Extract and read the JSON data from the uploaded .txt file silently.
2. Analyze the JSON data to identify key search terms and locations silently.
3. Access Google Maps & Places APIs for additional insights into the search data silently.
4. Analyze patterns in the search data to deduce lifestyle preferences and routine activities of the user silently.
5. Generate and output a JSON structure with preferences of the user based on the analysis. The output should strictly adhere to the specified JSON format without additional commentary.
`;

export function createAddressInstruction() {
  return `"homeAddress": "full Address", "workAddress": "full Address"`;
}

export function createTransportationInstruction() {
  return `
    "transportation": [
      {
        "method": "string",
        "selected": "boolean",
        "radius": "number"
      }
    ]
  `;
}

export function createCategoriesInstruction() {
  return `
    "categories": [
      {
        "title": "string",
        "userPreferences": "string",
        "environmentDescriptors": ["string"],
        "relatedSubcategories": ["string"],
        "costPreference": "string",
        "confidence": "number"
      }
    ]
  `;
}

export function createSocialPreferencesInstruction() {
  return `
    {
      "socialPreferences": [
        {
          "name": "string",
          "selected": "boolean",
          "description": "string"
        }
      ]
    }
  `;
}

// export function sendProfileRequest(instructionPart: string, file: File) {
//   const formData = new FormData();
//   formData.append("file", file);
//   formData.append("system_instruction", baseInstruction + task + instructionPart);

//   return fetch(`${process.env.REACT_APP_BACKEND_URL}/generate-profile`, {
//     method: "POST",
//     body: formData,
//   })
//     .then((res) => {
//       if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
//       return res.json();
//     })
//     .then((data) => {
//       console.log("Received JSON data:", data);
//       return data;
//     })
//     .catch((error) => {
//       console.error("Error:", error);
//       throw error;
//     });
// }

// ── Save to Firebase ──────────────────────────────────────────────────────────
/**
 * Saves analyzed survey data to Firebase Realtime Database IF a geocodable
 * location is present in the data.
 *
 * @param analyzedData  The full result from Gemini (must include lat + lng)
 * @param source        Upload method tag ("bulk_upload" | "direct_upload" | "picture_upload")
 * @param userId        Firebase UID from AuthContext
 * @returns             true if saved, false if skipped or failed
 */
export const saveOnboardingDataIfHasLocation = async (
  analyzedData: any,
  source: string,
  userId: string
): Promise<boolean> => {

  const hasCoords =
    typeof analyzedData.lat === "number" &&
    typeof analyzedData.lng === "number";

  if (!hasCoords) {
    console.log(`[${source}] No GPS coordinates found. Skipping Firebase upload.`);
    return false;
  }

  if (!userId) {
    console.error(`[${source}] No userId provided. Cannot save to Firebase.`);
    return false;
  }

  try {
    const sanitized = sanitizeRecord({
      ...analyzedData,
      sourceMethod: source,
      savedAt: new Date().toISOString(),
    });

    const onboardingRef = ref(database, `users/${userId}/onboardingData`);
    await push(onboardingRef, sanitized);

    console.log(`[${source}] ✅ Saved to Firebase at users/${userId}/onboardingData`);
    return true;
  } catch (error) {
    console.error(`[${source}] ❌ Firebase write failed:`, error);
    return false;
  }
};
